import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { Picker, selectPicker } from "features/render";

import { measureActions, selectObjectMeasureIds } from "./measureSlice";

/**
 * Tracks the object under the mouse for the object-to-object tool, which highlights it to show what
 * a click would measure.
 *
 * Picking takes time, so the answer arrives after the event that asked for it - by which point the
 * mouse may have moved on, left the canvas, or the tool may be gone. Every request is therefore
 * claimed with {@link begin} up front and only written by {@link settle} if it is still the newest
 * one and the tool is still picking. Without that, an older pick resolving late would put the
 * highlight back on an object the mouse has left, or revive it after it was deliberately dropped.
 */
export function useObjectMeasureHover() {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const picker = useAppSelector(selectPicker);

    // Monotonic: the newest claim wins, and anything that drops the hover bumps it so that picks
    // still in the air lose.
    const requestRef = useRef(0);

    const invalidate = useCallback(() => {
        requestRef.current += 1;
        return requestRef.current;
    }, []);

    // Leaving the tool has to supersede a pick in the air as well - and so does leaving and coming
    // straight back, since re-entering keeps the hover id on purpose (see the `setPicker` case in
    // `measureSlice`), which would otherwise let the previous session's pick land in the new one.
    useEffect(() => {
        invalidate();
    }, [picker, invalidate]);

    /** Claims the hover for this pointer event. Pass the token to {@link settle}. */
    const begin = useCallback(() => invalidate(), [invalidate]);

    /**
     * Writes what the pick found, unless it has been superseded or the tool has moved on. The
     * picker and the picks are re-read from the store rather than taken from the render the caller
     * was created in, which is already stale by the time a pick resolves.
     */
    const settle = useCallback(
        (request: number, objectId: number | undefined) => {
            if (requestRef.current !== request) {
                return;
            }

            const state = store.getState();
            if (selectPicker(state) !== Picker.ObjectMeasurement || selectObjectMeasureIds(state).length >= 2) {
                return;
            }

            dispatch(
                measureActions.setObjectMeasureHoverObjectId(objectId !== undefined && objectId >= 0 ? objectId : null),
            );
        },
        [dispatch, store],
    );

    /** Drops the hover, and with it anything still in the air - the pointer leaving, or a drag. */
    const clear = useCallback(() => {
        invalidate();
        dispatch(measureActions.setObjectMeasureHoverObjectId(null));
    }, [dispatch, invalidate]);

    return { begin, settle, clear };
}
